import { useEffect, useRef } from "react";
import type { FrameworkResponse } from "@patent-studio/api-types";
import { LoadFailure } from "@/components/shared/LoadFailure";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/Spinner";
import { useProposeFramework } from "@/hooks/use-dd-framework";

/**
 * Whether Praxis's proposal is still on its way, so the page holds the plan still for it. A run
 * the server reads as stale (stranded past its bound) is not: the page may ask again.
 */
export function proposalRunning(
  proposal: FrameworkResponse["proposal"],
): boolean {
  return proposal.status === "running" && !proposal.stale;
}

/** Whether the run ended without a framework: it failed, or the server reads it as stranded. */
function proposalFailed(proposal: FrameworkResponse["proposal"]): boolean {
  return (
    proposal.status === "failed" ||
    (proposal.status === "running" && proposal.stale)
  );
}

/** Whether the framework is empty and Praxis has never been asked: the page asks once, on the first open. */
function untouched(data: FrameworkResponse): boolean {
  return (
    (data.framework.sections ?? []).length === 0 &&
    (data.framework.removed ?? []).length === 0 &&
    data.proposal.status === "not_started"
  );
}

/** Ask Praxis for the first framework once, when the page first finds none. */
export function useFirstProposal(taskId: string, data: FrameworkResponse) {
  const propose = useProposeFramework(taskId);
  const askedRef = useRef(false);
  const needed = untouched(data);
  useEffect(() => {
    if (!needed || askedRef.current) return;
    askedRef.current = true;
    propose.mutate();
  }, [needed, propose]);
  return propose;
}

/**
 * Praxis's run in place of the sections: planning while it runs, and a failed first proposal
 * with the way to ask again. The attorney can always start with a section of their own.
 */
export function ProposalState({
  data,
  onRetry,
}: {
  data: FrameworkResponse;
  onRetry: () => void;
}) {
  const empty = (data.framework.sections ?? []).length === 0;
  if (proposalRunning(data.proposal) && empty) {
    return (
      <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner size="sm" /> Praxis is planning the Detailed Description…
      </div>
    );
  }
  if (!proposalFailed(data.proposal) || !empty) return null;
  return (
    <div className="mt-6 flex items-center gap-3">
      <LoadFailure
        error={null}
        fallback="The framework could not be proposed."
        className="flex-1"
      />
      <Button variant="secondary" size="dense" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
